import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getDisplayMessage } from '@/api/apiClient';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, authError, isLoadingAuth, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pendingRedirect = useRef(false);

  useEffect(() => {
    if (pendingRedirect.current && isAuthenticated) {
      pendingRedirect.current = false;
      const next = (searchParams.get('next') || '').trim() || '/Dashboard';
      const plano = (searchParams.get('plano') || '').trim();
      let target = next.startsWith('/') ? next : `/${next}`;
      if (plano) {
        target += (target.includes('?') ? '&' : '?') + `plano=${encodeURIComponent(plano)}`;
      }
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), senha);
      pendingRedirect.current = true;
    } catch (err) {
      setError(getDisplayMessage(err, 'Falha no login'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-slate-100 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url(/fundo-login.png)' }}
    >
      <Card className="w-full max-w-md shadow-xl border-none bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <img 
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68e50deef22388676f24e5ca/058df03c3_LogoAuction.png"
              alt="Auction Logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">Auction - Licitações</CardTitle>
          <CardDescription className="mt-2">Entre com seu e-mail e senha</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {(authError?.message || error) && (
              <Alert variant="destructive">
                <AlertDescription>{authError?.message || error}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="senha">Senha</Label>
              <Input
                id="senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                className="mt-1"
              />
              <p className="mt-1 text-right">
                <Link to="/EsqueciSenha" className="text-sm text-blue-600 hover:underline">
                  Esqueci minha senha
                </Link>
              </p>
            </div>
            <Button type="submit" className="w-full" disabled={loading || isLoadingAuth}>
              {loading || isLoadingAuth ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
